/**
 * @file The one place an `appointment_booking_link_notification` payload is built.
 * @module utils/healthcare/bookingLinkNotification
 *
 * The sibling of `bookingLink.ts`: that module owns the URL, this one owns the payload the URL
 * travels in. Modelled directly on `utils/forms/formAssignedNotification`, which is the platform's
 * only other "link in a message" mechanism.
 */

import { format } from "date-fns";

import { Patient } from "../../types/healthcare/PatientTypes";
import { SendCommunicationRequest } from "../../types/communications/CommunicationTypes";
import { buildPatientBookingLink } from "./bookingLink";
import type { UserUuid } from "../../types/identity";

/**
 * ⚠️ Cross-repo constant with no mechanical guard — must match the notification service's
 * `AppointmentBookingLinkTemplateSeeder::CATEGORY` byte for byte. A typo falls through to no
 * template at all, and the failure is quiet.
 */
export const BOOKING_LINK_CATEGORY = "appointment_booking_link_notification";

/**
 * Which contact detail to use. Unlike the forms payload there is no `both` default: the modal
 * offers "Send by email" and "Send by SMS" as two separate acts, and a practitioner who picks one
 * must not also send the other.
 */
export type BookingLinkChannel = "email" | "sms";

export interface BookingLinkNotificationInput {
	/** The token from the 201 — the SAME one already on screen, never a re-fetch. */
	token: string;
	patient?: Partial<Patient> | null;
	expiresAt?: string | null;
	recipientUserId: UserUuid;
	channel: BookingLinkChannel;
	companyId?: number;
	userId?: UserUuid;
}

/**
 * The expiry as the patient should read it, never as an ISO timestamp.
 *
 * An unparseable or absent value yields `""` rather than being omitted: an omitted key leaves the
 * literal `{{expires_at}}` in the message, which is strictly worse than a slightly clipped
 * sentence. The backend has no "never expires" path, so the empty case should not occur.
 */
const formatExpiry = (expiresAt?: string | null): string => {
	if (!expiresAt) return "";

	try {
		return format(new Date(expiresAt), "d MMM yyyy, h:mm a");
	} catch {
		return expiresAt;
	}
};

/**
 * The `data` bag. ⚠️ The patient is spread FIRST and `booking_link` LAST, so a patient record
 * that happens to carry a `booking_link` column can never shadow the link just built.
 */
const bookingLinkData = ({ patient, token, expiresAt }: BookingLinkNotificationInput) => ({
	...(patient ?? {}),
	expires_at: formatExpiry(expiresAt),
	booking_link: buildPatientBookingLink(token),
});

/** Pass the result straight to `sendCommunication`. */
export const buildBookingLinkCommunication = (
	input: BookingLinkNotificationInput,
): SendCommunicationRequest => ({
	recipient_user_id: input.recipientUserId,
	category: BOOKING_LINK_CATEGORY,
	// One channel, explicitly. Omitting this would let the service dispatch on every channel the
	// patient has enabled — i.e. "Send by SMS" would also email.
	channels: [input.channel],
	company_id: input.companyId,
	user_id: input.userId,
	data: bookingLinkData(input),
});
